// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { isEqual, get, noop } from "lodash";
import { graphql } from "@apollo/client/react/hoc";
import mediator from "@tvg/mediator";
import type { Dispatch } from "redux";
import { formatWroBets } from "@tvg/utils/pastPerformanceUtils";
import { getFilteredRaces } from "@fdr/utils/pastPerformanceUtils";
import type { BinaryFn, NullaryFn, UnaryFn } from "@tvg/types/Functional";
import type { WagerProfile } from "@tvg/types/User";
import type { Device } from "@tvg/types/Device";
import type { RaceCardRunner, RaceProgram } from "@tvg/types/Race";
import type { PastPerformanceRace } from "@fdr/types/PastPerformance";
import type { GeolocationStatus } from "@tvg/sh-geolocation/src/types";
import type { ToastProps } from "@fdr/utils/generalTypes";
import wro from "@tvg/api/wro";
import { formatPastRaceDate } from "@tvg/formatter/dates";
import type { TabKey } from "@tvg/atomic-ui/_templates/RaceOfficials";
import InlinePastPerformance from "@fdr/atomic-ui/_templates/InlinePastPerformance";
import ModalV2 from "@fdr/atomic-ui/_templates/ModalV2";
import type { RaceBet } from "@tvg/atomic-ui/_organism/RaceBets";
import Toast from "@fdr/atomic-ui/_atoms/Toast";
import { parseJSONCapiMessage } from "@fdr/utils/parseJSONCapiMessage";
import { isXSell } from "@tvg/sh-utils/mobileUtils";

import {
  getGeolocationMessageShown,
  getGeolocationStatus
} from "@tvg/sh-geolocation/src/redux/selectors";
import { getAccountNumber } from "@urp/store-selectors";
import { geolocationStatusClear } from "@tvg/sh-geolocation/src/redux/actions";
import PastPerformance from "./PastPerformance";
import InlinePastPerformanceQuery from "./graphql/queries/inlinePastPerformance.graphql";
import { ApolloOptionsIPP } from "./graphql/options.graph";

type Props = {
  isLoading: boolean,
  entityRunnerId: string,
  runnerName: string,
  originRaceCode: string,
  originRaceNumber: string,
  originRaceConditions: {
    distance: ?string,
    surface: ?string
  },
  races: PastPerformanceRace[],
  raceIds: string[],
  ippEmptyStateMsg: string,
  wagerProfile: WagerProfile,
  fcpClient: mixed,
  behgClient: mixed,
  isLogged: boolean,
  onModalOpen: NullaryFn<void>,
  device: Device,
  hasNavigation: boolean,
  onTabClick: UnaryFn<TabKey, boolean>,
  accountNumber: string,
  onModalClose: NullaryFn<void>,
  isModalOpen: boolean,
  originRace: ?RaceProgram,
  runners: RaceCardRunner[],
  onRunnerClick: BinaryFn<string, string, void>,
  hasWebSockets: boolean,
  geolocationStatus?: GeolocationStatus,
  geolocationMessageShown?: boolean,
  geoLocationToastMsg?: { [key: GeolocationStatus]: ToastProps },
  dispatch: Dispatch<*>,
  behgToggle: boolean,
  raceBets: ?(RaceBet[])
};

type State = {
  pastPerformanceRace: ?PastPerformanceRace,
  pastPerformanceRaceIndex: number,
  hasPreviousRace: boolean,
  hasNextRace: boolean,
  raceListView: boolean,
  isRaceFromRaceListView: boolean,
  isRaceListMatched: boolean,
  runner: ?RaceCardRunner,
  selectionFromList: boolean,
  raceBets: ?(RaceBet[])
};

export class InlinePastPerformanceComponent extends Component<Props, State> {
  static defaultProps = {
    races: [],
    raceIds: [],
    entityRunnerId: "",
    runnerName: "",
    originRaceCode: "",
    originRaceNumber: "",
    originRaceConditions: {
      distance: null,
      surface: null
    },
    isLoading: true,
    ippEmptyStateMsg: "",
    fcpClient: noop,
    behgClient: noop,
    wagerProfile: "PORT-Generic",
    isLogged: false,
    onModalOpen: noop,
    device: "mobile",
    hasNavigation: false,
    onTabClick: noop,
    accountNumber: "",
    onModalClose: noop,
    isModalOpen: false,
    originRace: null,
    runners: [],
    onRunnerClick: noop,
    hasWebSockets: false,
    geolocationStatus: undefined,
    geolocationMessageShown: false,
    geoLocationToastMsg: {}
  };

  races: PastPerformanceRace[] = [];

  filteredRaces: PastPerformanceRace[] = [];

  filteredRacesIds: string[] = [];

  constructor(props: Props) {
    super(props);

    this.state = {
      pastPerformanceRace: null,
      pastPerformanceRaceIndex: -1,
      hasPreviousRace: false,
      hasNextRace: false,
      raceListView: true,
      isRaceFromRaceListView: false,
      isRaceListMatched: false,
      runner: null,
      selectionFromList: false,
      raceBets: null
    };

    this.races = this.props.races;

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: true
      }
    });
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const {
      races,
      isLoading,
      isLogged,
      isModalOpen,
      runnerName,
      entityRunnerId
    } = this.props;

    return (
      races.length !== nextProps.races.length ||
      isLoading !== nextProps.isLoading ||
      isModalOpen !== nextProps.isModalOpen ||
      isLogged !== nextProps.isLogged ||
      runnerName !== nextProps.runnerName ||
      entityRunnerId !== nextProps.entityRunnerId ||
      nextState.selectionFromList !== this.state.selectionFromList ||
      nextState.isRaceListMatched !== this.state.isRaceListMatched ||
      !isEqual(this.props.runners, nextProps.runners) ||
      !isEqual(this.state, nextState) ||
      !isEqual(this.props.originRace, nextProps.originRace) ||
      !isEqual(races, nextProps.races)
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.isLoading !== prevProps.isLoading) {
      this.races = this.props.races;

      const filteredResults = getFilteredRaces(
        this.props.races,
        this.props.originRaceConditions
      );
      this.filteredRaces = filteredResults.races;
      this.filteredRacesIds = filteredResults.ids;

      this.races = this.state.isRaceListMatched
        ? this.filteredRaces
        : this.props.races;

      this.forceUpdate();
    }

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: true
      }
    });
  }

  componentWillUnmount() {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:UPDATE_RACE_CARD",
      payload: {
        delayed: false
      }
    });
  }

  onMatchConditions = () => {
    const isRaceListMatched = !this.state.isRaceListMatched;
    this.sendMatchingConditionsGTMEvent(
      isRaceListMatched,
      `${get(this.props.originRaceConditions, "distance")} & ${get(
        this.props.originRaceConditions,
        "surface"
      )}`
    );
    const newState = { ...this.state, isRaceListMatched };
    this.races = isRaceListMatched ? this.filteredRaces : this.props.races;
    this.setState(newState);
  };

  onRace = (raceIndex: number) => {
    if (!this.props.isLogged) {
      this.performActionWithLogin(this.setRace.bind(this, raceIndex));
    } else {
      this.setRace(raceIndex);
    }
  };

  onRaceFromList = (raceIndex: number) => {
    this.setState(
      {
        raceListView: false,
        selectionFromList: true,
        isRaceFromRaceListView: true
      },
      () => {
        this.setRace(raceIndex);
      }
    );
  };

  onRaceClose = () => {
    if (this.state.raceListView) {
      this.sendCloseAllPPGTMEvent();
    } else {
      this.sendClosePPGTMEvent();
    }

    this.props.onModalClose();

    this.races = this.props.races;

    this.setState({
      pastPerformanceRace: null,
      pastPerformanceRaceIndex: -1,
      hasPreviousRace: false,
      hasNextRace: false,
      raceListView: true,
      isRaceFromRaceListView: false,
      isRaceListMatched: false,
      runner: null,
      selectionFromList: false,
      raceBets: null
    });
  };

  onBackModal = () => {
    const { runner, pastPerformanceRace } = this.state;

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:BACK_TO_ALL",
      payload: {
        trackName: get(pastPerformanceRace, "track.name", ""),
        runnerName: get(runner, "horseName", ""),
        raceNumber: get(pastPerformanceRace, "number", "")
      }
    });

    this.setState({
      raceListView: true,
      isRaceFromRaceListView: false
    });
  };

  setPreviousRace = () => {
    const { pastPerformanceRaceIndex, hasPreviousRace } = this.state;

    if (hasPreviousRace) {
      this.sendNavigationGTMEvent("Previous");
      this.setRace(pastPerformanceRaceIndex - 1);
    }
  };

  setNextRace = () => {
    const { pastPerformanceRaceIndex, hasNextRace } = this.state;

    if (hasNextRace) {
      this.sendNavigationGTMEvent("Next");
      this.setRace(pastPerformanceRaceIndex + 1);
    }
  };

  setRace = (raceIndex: number, raceListView: boolean = false) => {
    const pastPerformanceRace = get(this.races, `[${raceIndex}]`, null);
    const pastPerformanceRaceIndex = pastPerformanceRace ? raceIndex : -1;
    const hasPreviousRace = pastPerformanceRaceIndex > 0;
    const hasNextRace = pastPerformanceRaceIndex < this.races.length - 1;

    this.props.onModalOpen();
    this.setState(
      {
        pastPerformanceRace,
        pastPerformanceRaceIndex,
        hasPreviousRace,
        hasNextRace,
        raceListView
      },
      this.fetchBets
    );
  };

  getRaceBets = () => {
    const date = formatPastRaceDate(
      get(this.state, "pastPerformanceRace.date", "")
    );

    return wro
      .getMyBets(
        {
          startDate: date,
          endDate: date,
          track: get(this.state, "pastPerformanceRace.track.code"),
          race: +get(this.state, "pastPerformanceRace.number")
        },
        this.props.accountNumber
      )
      .then((res) => (res.status === 200 ? formatWroBets(res) : []))
      .catch(() => []);
  };

  fetchBets = () => {
    if (this.state.pastPerformanceRace && !this.props.behgToggle) {
      this.getRaceBets().then((res) => this.setState({ raceBets: res }));
    }
  };

  performActionWithLogin = (callback: NullaryFn<*>) => {
    mediator.base.dispatch({
      type: "OPEN_LOGIN",
      payload: {
        callback
      }
    });
  };

  sendClickGTMEvent = (runner: RaceCardRunner) => {
    if (this.state.selectionFromList) {
      mediator.base.dispatch({
        type: "PAST_PERFORMANCES:SELECT_PP_MORE_PAGE",
        payload: {
          trackName: get(this.state.pastPerformanceRace, "track.name", ""),
          runnerName: runner.horseName
        }
      });
    } else {
      mediator.base.dispatch({
        type: "PAST_PERFORMANCES:CLICK_PP",
        payload: {
          trackName: get(this.state.pastPerformanceRace, "track.name", ""),
          runnerName: runner.horseName,
          raceNumber: get(this.state.pastPerformanceRace, "raceNumber", "")
        }
      });
    }
  };

  sendNavigationGTMEvent = (direction: string) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:NAVIGATE_PP",
      payload: {
        direction,
        trackName: get(this.state.pastPerformanceRace, "track.name", ""),
        runnerName: get(this.state.runner, "horseName", ""),
        raceNumber: get(this.state.pastPerformanceRace, "raceNumber", "")
      }
    });
  };

  sendCloseAllPPGTMEvent = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:CLOSE_ALL_PP"
    });
  };

  sendClosePPGTMEvent = () => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:CLOSE_SINGLE_PP"
    });
  };

  sendMatchingConditionsGTMEvent = (
    isToggleEnabled: boolean,
    matchingConditions: string
  ) => {
    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:TOGGLE_CONDITIONS",
      payload: {
        toggleState: isToggleEnabled ? "On" : "Off",
        matchingConditions
      }
    });
  };

  updateRunner = (runner: RaceCardRunner) => {
    this.setState({ runner });
  };

  renderModal = () => {
    const {
      pastPerformanceRace,
      pastPerformanceRaceIndex,
      raceListView,
      isRaceFromRaceListView
    } = this.state;

    const isOpen = this.props.isModalOpen;
    const onBack =
      this.props.hasNavigation && isRaceFromRaceListView
        ? this.onBackModal
        : null;

    const openRunnerDetails = isOpen && !raceListView && !!pastPerformanceRace;

    const modalProps = {
      ...{
        hasShadow: openRunnerDetails,
        isLastModal: true,
        isOpen,
        onClose: this.onRaceClose,
        modalTopChildren: () =>
          !isXSell() &&
          this.props.geolocationStatus &&
          this.props.geoLocationToastMsg &&
          this.props.geoLocationToastMsg[this.props.geolocationStatus] && (
            <Toast
              {...this.props.geoLocationToastMsg[this.props.geolocationStatus]}
              noAnimation={this.props.geolocationMessageShown}
              callback={() => this.props.dispatch(geolocationStatusClear())}
              renderBottom
              isTopPosition
            />
          )
      },
      ...{
        title: !openRunnerDetails ? "Race Log and Replays" : "Race Replay",
        subtitle: openRunnerDetails ? this.props.runnerName : "",
        isSubTitleUppercase: !openRunnerDetails,
        animation: "bottom",
        onBack
      }
    };

    let modalContent;

    if (isOpen && raceListView) {
      modalContent = (
        <InlinePastPerformance
          activeRunner={this.props.runnerName}
          isLoadingLog={this.props.isLoading}
          // $FlowFixMe WIll fix this we have a breaking bug in tvg tyoes
          runners={this.props.runners}
          onRaceClose={this.onRaceClose}
          onRunnerClick={this.props.onRunnerClick}
          races={this.races}
          onRace={this.onRaceFromList}
          paddingless
          borderless
          onMatchToggle={this.onMatchConditions}
          isMatchConditionsOn={this.state.isRaceListMatched}
          originRace={`${this.props.originRaceCode} - R${this.props.originRaceNumber}`}
          isMobile
          currentRace={this.props.originRace}
        />
      );
    } else if (isOpen && !!pastPerformanceRace) {
      modalContent = (
        <PastPerformance
          device={this.props.device}
          races={this.races}
          selectedRace={pastPerformanceRace}
          selectedRaceIndex={pastPerformanceRaceIndex}
          runnerName={this.props.runnerName}
          trackCode={get(pastPerformanceRace, "track.code", "")}
          raceNumber={get(pastPerformanceRace, "raceNumber", "")}
          raceDate={formatPastRaceDate(
            get(pastPerformanceRace, "raceDate", "")
          )}
          wagerProfile={this.props.wagerProfile}
          fcpClient={this.props.fcpClient}
          behgClient={this.props.behgClient}
          hasPreviousRace={this.state.hasPreviousRace}
          hasNextRace={this.state.hasNextRace}
          onPreviousRaceNavigation={this.setPreviousRace}
          onNextRaceNavigation={this.setNextRace}
          onRaceFromList={noop}
          isRaceListView={this.state.raceListView}
          onClickGTMEvent={this.sendClickGTMEvent}
          updateRunner={this.updateRunner}
          onMatchConditions={this.onMatchConditions}
          isMatchConditionsOn={this.state.isRaceListMatched}
          accountNumber={this.props.accountNumber}
          originRace={`${this.props.originRaceCode} - R${this.props.originRaceNumber}`}
          hasWebSockets={this.props.hasWebSockets}
          {...(!this.props.behgToggle ? { raceBets: this.state.raceBets } : {})}
        />
      );
    }

    return (
      <ModalV2 {...modalProps}>
        {/* istanbul ignore next */ () => modalContent}
      </ModalV2>
    );
  };

  render() {
    return this.renderModal();
  }
}

export default connect(
  /* istanbul ignore next */
  (store) => ({
    isLogged: get(store, "userData.logged", false),
    ippEmptyStateMsg: get(store, "capi.messages.ippEmptyStateMsg", false),
    accountNumber: getAccountNumber(store),
    hasNavigation: get(
      store,
      "capi.featureToggles.inLinePastPerformanceNavigation",
      false
    ),
    geolocationStatus: getGeolocationStatus(store),
    geolocationMessageShown: getGeolocationMessageShown(store),
    geoLocationToastMsg: parseJSONCapiMessage(
      store,
      "capi.messages.geoLocationToastMsg",
      {}
    ),
    behgToggle: get(store, "capi.featureToggles.myBetsBehgConnection", false)
  }),
  /* istanbul ignore next */
  (dispatch: Dispatch<*>) => ({ dispatch })
)(
  graphql(
    InlinePastPerformanceQuery,
    ApolloOptionsIPP
  )(InlinePastPerformanceComponent)
);
